import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import '../../business/Business.css';
import './PaymentProviderDetailsPage.css';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import CreateFooter from '../../business/components/CreateFooter';
import DeleteProductModal from '../../dashboard/components/product_item_components/DeleteProductModal';
import { TrashIcon } from '../../dashboard/components/product_item_components/icons';
import { PaymentProviderTypes } from '../../../types/PaymentProviderTypes';

function PaymentProviderDetailsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { loading, requestWithMeta } = useHttp();

  const [providerId, setProviderId] = React.useState(null);
  const [sid, setSid] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');
  const [testMode, setTestMode] = React.useState(false);
  const [loadingDetails, setLoadingDetails] = React.useState(true);
  const [submitError, setSubmitError] = React.useState(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const listPath = `/branch/${branchId}/payment-providers`;

  React.useEffect(() => {
    if (!branchId || branchId === 'store') {
      setLoadingDetails(false);
      setSubmitError('Выберите филиал');
      return;
    }

    let cancelled = false;
    setLoadingDetails(true);
    setSubmitError(null);
    (async () => {
      try {
        const res = await requestWithMeta(
          `/operator/branches/${encodeURIComponent(branchId)}/payment-providers`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить способ оплаты');
          return;
        }
        const list = Array.isArray(res?.data?.paymentProviders) ? res.data.paymentProviders : [];
        const existing = list.find((row) => Number(row.providerType) === PaymentProviderTypes.O_TRADE);
        if (!existing) return;
        setProviderId(existing.id ?? existing._id ?? null);
        setSid(String(existing.credentials?.sid ?? ''));
        setTestMode(Number(existing.credentials?.test) === 1);
        setSecretKey('');
      } catch (_) {
        if (!cancelled) setSubmitError('Ошибка загрузки');
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    })();

    return () => { cancelled = true; };
  }, [branchId, requestWithMeta]);

  const isEdit = providerId != null;
  const secretRequired = !isEdit;
  const saveDisabled =
    loading ||
    loadingDetails ||
    deleting ||
    !sid.trim() ||
    (secretRequired && !secretKey.trim());

  const handleSave = async () => {
    setSubmitError(null);
    const credentials = {
      sid: sid.trim(),
      test: testMode ? 1 : 0,
    };
    if (secretKey.trim()) credentials.secretKey = secretKey.trim();

    if (isEdit) {
      const res = await requestWithMeta(
        `/operator/payment-providers/${encodeURIComponent(providerId)}`,
        'PUT',
        { providerType: PaymentProviderTypes.O_TRADE, credentials }
      );
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить способ оплаты');
        return;
      }
      setSecretKey('');
      navigate(listPath);
      return;
    }

    const res = await requestWithMeta(
      `/operator/branches/${encodeURIComponent(branchId)}/payment-providers`,
      'POST',
      { providerType: PaymentProviderTypes.O_TRADE, credentials }
    );
    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить способ оплаты');
      return;
    }
    navigate(listPath);
  };

  const handleConfirmDelete = async () => {
    if (providerId == null) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      const res = await requestWithMeta(
        `/operator/payment-providers/${encodeURIComponent(providerId)}`,
        'DELETE'
      );
      if (res?.ok) {
        setConfirmOpen(false);
        navigate(listPath);
        return;
      }
      setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить способ оплаты');
    } catch (_) {
      setDeleteError('Ошибка удаления');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="shop-create-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="O!Trade"
          subtitle={isEdit ? 'Измените данные подключения' : 'Укажите данные подключения'}
          onBack={() => navigate(listPath)}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="otrade-sid">
              SID <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="otrade-sid"
              className="shop-create-input"
              value={sid}
              onChange={(e) => setSid(e.target.value)}
              autoComplete="off"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="otrade-secret">
              Секретный ключ {secretRequired ? <b style={{ color: 'red' }}>*</b> : null}
            </label>
            <input
              id="otrade-secret"
              className="shop-create-input"
              type="password"
              value={secretKey}
              placeholder={isEdit ? 'Оставьте пустым, чтобы не менять' : ''}
              onChange={(e) => setSecretKey(e.target.value)}
              autoComplete="new-password"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="payment-provider-check" htmlFor="otrade-test">
              <input
                id="otrade-test"
                type="checkbox"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                disabled={loadingDetails}
              />
              Тестовый режим
            </label>
          </section>
        </main>

        <CreateFooter
          disabled={saveDisabled}
          label={loading ? 'Сохранение…' : 'Сохранить'}
          onCreate={handleSave}
          leading={isEdit ? (
            <button
              type="button"
              className="payment-provider-deleteButton"
              aria-label="Удалить O!Trade"
              title="Удалить"
              disabled={loading || deleting || loadingDetails}
              onClick={() => {
                setDeleteError(null);
                setConfirmOpen(true);
              }}
            >
              <TrashIcon />
            </button>
          ) : null}
        />
      </div>

      <DeleteProductModal
        isOpen={confirmOpen}
        onClose={() => {
          if (deleting) return;
          setConfirmOpen(false);
          setDeleteError(null);
        }}
        title="Удалить способ оплаты"
        productName="O!Trade"
        deleteError={deleteError}
        loading={deleting}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default PaymentProviderDetailsPage;
